import React, { useState, useRef, useEffect } from "react";
import { useDispatch } from "react-redux";
import { motion } from "framer-motion";
import swal from "sweetalert";
import { useHistory } from "react-router-dom";

import Select from "react-select";
import makeAnimated from "react-select/animated";

import { Button } from "../../elements/Button";
import commoncss from "../../css/common.module.css";
import buttoncss from "../../css/button.module.css";
import { ReactComponent as FilterIcon } from "../../assets/icons/filter.svg";
import { ReactComponent as PlusIcon } from "../../assets/icons/AddPlus.svg";
import { ReactComponent as ExportIcon } from "../../assets/icons/download.svg";
import { ReactComponent as CirclePlus } from "../../assets/icons/circlePlus.svg";
import { ReactComponent as AddEditor } from "../../assets/icons/addEditor.svg";
import { ReactComponent as AddTable } from "../../assets/icons/addTable.svg";
import libcss from "../../css/library.module.css";
import {
  createDisclosureApiHandler,
  createSubDisclosureApiHandler,
  updateDisclosureApiHandler,
  getTemplateDetailsApiHandler,
  updateSubDisclosureApiHandler,
  deleteDisclosureApiHandler,
  deleteSubDisclosureApiHandler,
} from "../../../WorkSpace/Components/ApiServices/apiHandler";
import { ReactComponent as Plus } from "../../../myfin_v2/assets/icons/plusprimary.svg";
import { ReactComponent as EditIcon } from "../../../myfin_v2/assets/icons/editPri.svg";
import { ReactComponent as CloseIcon } from "../../../myfin_v2/assets/icons/close.svg";
import { ReactComponent as DelIcon } from "../../../myfin_v2/assets/icons/delete_red.svg";
import ControlledTableGenerator from "./TemplateTableGenerator";
import QuillReactEditor from "../../components/QuillReactEditor";

const genId = () => Math.random().toString(36).slice(2);

const normalizeFromInitial = (initialDoc) => {
  const items = [];
  (initialDoc?.data || []).forEach((block) => {
    if (block.dataType === "text") {
      items.push({
        id: genId(),
        type: "text",
        dataType: "text",
        textFormat: "html",
        textRaw: block?.textRaw ?? " ",
        textPlain: block?.textPlain ?? " ",
        text: [
          {
            id: genId(),
            content: block?.textRaw ?? " ",
          },
        ],
      });
    } else if (block.dataType === "table") {
      items.push({
        id: genId(),
        type: "table",
        tableTitle: block.tableTitle || "Untitled Table",
        tableRow: Array.isArray(block.tableRow)
          ? block.tableRow.map((r) => ({ ...r }))
          : [],
        tableColumn: Array.isArray(block.tableColumn)
          ? block.tableColumn.map((c) => ({ ...c }))
          : [],
      });
    }
  });
  return items;
};

// Serialize the flat list back to your exact schema
const serialize = (name, items) => {
  const data = items.map((it) => {
    if (it.type === "text") {
      return {
        dataType: "text",
        textFormat: it.textFormat,
        textRaw: it?.textRaw ?? " ",
        textPlain: it?.textPlain ?? " ",
      };
    }
    if (it.type === "table") {
      return {
        dataType: "table",
        table: [],
        tableTitle: it.tableTitle || "Untitled Table",
        tableRow: it.tableRow || [],
        tableColumn: it.tableColumn || [],
      };
    }
    return it;
  });

  return { name, data };
};

const removeIdsAndFormulas = (obj) => {
  if (Array.isArray(obj)) {
    return obj.map((item) => removeIdsAndFormulas(item));
  }

  if (typeof obj === "object" && obj !== null) {
    const newObj = {};

    Object.keys(obj).forEach((key) => {
      if (key === "_id" || key === "formula") {
        return;
      }

      newObj[key] = removeIdsAndFormulas(obj[key]);
    });

    return newObj;
  }

  return obj;
};

// export default function ({ templateData, tempId }){
export default function Disclosures({
  tempId,
  setHandleSave,
  handleSave,
  isReadOnly = false,
}) {
  const [name, setName] = useState("");
  const [items, setItems] = useState(() => normalizeFromInitial(null));
  const [output, setOutput] = useState(null);
  const [forceOpenTableId, setForceOpenTableId] = useState(null);

  const animatedComponents = makeAnimated();
  let history = useHistory();
  const dispatch = useDispatch();
  const [libraryName, setLibraryName] = useState("ST Library");
  const [activeItem, setActiveItem] = useState(null);
  const [selNote, setSelNote] = useState(null);
  const [selNoteId, setSelNoteId] = useState(null);
  const [selParentId, setSelParentId] = useState(null); // <-- NEW: parent id for subnotes
  const [notes, setNotes] = useState(null);
  const [note, setNote] = useState(null);
  const [hoveredIndex, setHoveredIndex] = useState(null);

  const [isComposing, setIsComposing] = useState(false);
  const [noteName, setNoteName] = useState("");
  const inputRef = useRef(null);

  const [templateData, setTemplateData] = useState(null);

  const getTemplateDetails = () => {
    getTemplateDetailsApiHandler({ groupingTemplateID: tempId })
      .then((response) => {
        if (response?.data?.success === true) {
          setTemplateData(response?.data?.groupingTemplate);
        }
      })
      .catch((error) => {
        // ignore
      });
  };
  useEffect(() => {
    getTemplateDetails();
  }, []);
  useEffect(() => {
    getTemplateDetails();
  }, [activeItem]);

  // Outside controls only
  const handleAddParagraph = () => {
    setItems((prev) => [
      ...prev,
      {
        id: genId(),
        type: "text",
        text: [{ id: genId(), content: "" }],
        textFormat: "html",
        textRaw: " ",
        textPlain: " ",
      },
    ]);
  };

  const handleAddTable = () => {
    const id = genId();
    setItems((prev) => [
      ...prev,
      {
        id,
        type: "table",
        tableTitle: "",
        tableRow: [],
        tableColumn: [],
      },
    ]);
    setForceOpenTableId(id);
  };

  const removeItem = (id) =>
    setItems((prev) => prev.filter((it) => it.id !== id));

  const updateEditorContent = (id, html) => {
    setItems((prev) =>
      prev.map((it) =>
        it.id === id
          ? {
              ...it,
              text: [{ id: it.text?.[0]?.id ?? genId(), content: html }],
              textFormat: "html",
              textRaw: html?.html ?? " ",
              textPlain: html?.text ?? " ",
            }
          : it
      )
    );
  };

  const updateTableItem = (id, patch) => {
    setItems((prev) =>
      prev.map((it) => (it.id === id ? { ...it, ...patch } : it))
    );
  };

  useEffect(() => {
    setOutput(serialize(name, items));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [name, items]);

  useEffect(() => {
    if (isComposing && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isComposing]);

  const handleAddClick = () => {
    setIsComposing(true);
    setNoteName("");
  };

  const handleCancel = () => {
    setIsComposing(false);
    setNoteName("");
  };

  const handlePost = () => {
    const value = noteName.trim();
    if (!value) return;
    setNote([value, ...note]);
    setNoteName("");
    setIsComposing(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") handlePost();
    if (e.key === "Escape") handleCancel();
  };

  const [subComposeFor, setSubComposeFor] = useState(null); // parent note index
  const [subNoteName, setSubNoteName] = useState("");
  const subInputRef = useRef(null);

  useEffect(() => {
    if (subComposeFor !== null && subInputRef.current) {
      subInputRef.current.focus();
    }
  }, [subComposeFor]);

  const openSubComposer = (noteIndex) => {
    setSubComposeFor(noteIndex);
    setSubNoteName("");
  };

  const cancelSubComposer = () => {
    setSubComposeFor(null);
    setSubNoteName("");
  };

  const postSubnote = () => {
    const value = subNoteName.trim();
    if (!value || subComposeFor === null) return;
    setNotes((prev) => {
      const next = [...prev];
      const target = { ...next[subComposeFor] };
      const subNotes = Array.isArray(target.subNotes)
        ? [...target.subNotes]
        : [];
      subNotes.push({ name: value });
      target.subNotes = subNotes;
      next[subComposeFor] = target;
      return next;
    });
    setSubNoteName("");
    setSubComposeFor(null);
  };

  const addSub = (note_id) => {
    const value = subNoteName.trim();
    if (!value) return;

    const generalNotesPayload = {
      name: subNoteName,
    };
    createSubDisclosureApiHandler(
      { groupingTemplateID: tempId, disclosureID: note_id },
      generalNotesPayload
    )
      .then((response) => {
        if (response?.data?.success === true) {
          setSubNoteName("");
          setSubComposeFor(null);
          setNotes(response?.data?.updatedGroupingTemplate?.disclosure);
        }
      })
      .catch((error) => {
        // ignore
      });
  };

  const handleSubKeyDown = (e) => {
    if (e.key === "Enter") postSubnote();
    if (e.key === "Escape") cancelSubComposer();
  };

  const breadcrumbItems = [
    {
      label: "Template Library",
      link: `/preview`,
      className: `${commoncss.nav_cnt_color} clickable`,
    },
    { label: `${libraryName}`, link: "", className: "headerLable" },
  ];

  // SAVE handler: supports subnote update via updateSubDisclosureApiHandler
  if (handleSave === "disclosures") {
    setHandleSave(null);
    const payload = removeIdsAndFormulas(output);

    if (selNote === "general_sn") {
      // Sub-note update: send parent disclosure id + sub disclosure id
      updateSubDisclosureApiHandler(
        {
          groupingTemplateID: templateData._id,
          disclosureID: selParentId,
          subNoteID: selNoteId,
        },
        payload
      )
        .then((response) => {
          if (response?.data?.success === true) {
            swal("Data saved successfully", "", "success");
            getTemplateDetails();
          } else {
            swal(
              "Error",
              response?.data?.error || "Unable to save the data",
              "error"
            );
          }
        })
        .catch((error) => {
          swal(
            "Error",
            error?.response?.data?.error || "Unable to save the data",
            "error"
          );
        });
    } else {
      // Top-level note update (existing behavior)
      updateDisclosureApiHandler(
        { groupingTemplateID: templateData._id, disclosureID: selNoteId },
        payload
      )
        .then((response) => {
          if (response?.data?.success === true) {
            swal("Data saved successfully", "", "success");
            getTemplateDetails();
          } else {
            swal(
              "Error",
              response?.data?.error || "Unable to save the data",
              "error"
            );
          }
        })
        .catch((error) => {
          swal(
            "Error",
            error?.response?.data?.error || "Unable to save the data",
            "error"
          );
        });
    }
  }

  useEffect(() => {
    setNotes(templateData?.disclosure);
  }, [templateData]);

  const addNotes = () => {
    const value = noteName.trim();
    if (!value) return;

    const generalNotesPayload = {
      name: noteName,
    };
    createDisclosureApiHandler(
      { groupingTemplateID: tempId },
      generalNotesPayload
    )
      .then((response) => {
        if (response?.data?.success === true) {
          setNoteName("");
          setIsComposing(false);
          setNotes(response?.data?.updatedGroupingTemplate?.disclosure);
        }
      })
      .catch((error) => {
        // ignore
      });
  };

  const resetItems = () => {
    setItems(normalizeFromInitial(null));
    setActiveItem(null);
    setSelNote(null);
    setSelNoteId(null);
    setSelParentId(null); // NEW: parent id for subnotes
  };
  const deleteNoteHandler = (id) => {
    swal({
      title: "Confirm deletion of disclosure data?",
      text: "Once deleted, you will not be able to recover this!",
      icon: "warning",
      buttons: {
        cancel: "Cancel",
        confirm: {
          text: "Delete",
          value: true,
          visible: true,
          className: "swal-delete-btn",
          closeModal: true,
        },
      },
      dangerMode: true,
    }).then((willDelete) => {
      if (willDelete) {
        deleteDisclosureApiHandler({
          groupingTemplateID: tempId,
          disclosureID: id,
        })
          .then((response) => {
            if (response?.data?.success === true) {
              resetItems();
              getTemplateDetails();
            }
          })
          .catch((error) => {
            swal(
              "Error",
              error?.response?.data?.error || "Unable to delete the data",
              "error"
            );
          });
      }
    });
  };

  const deleteSubNoteHandler = (id, subid) => {
    swal({
      title: "Confirm deletion of disclosure data?",
      text: "Once deleted, you will not be able to recover this!",
      icon: "warning",
      buttons: {
        cancel: "Cancel",
        confirm: {
          text: "Delete",
          value: true,
          visible: true,
          className: "swal-delete-btn",
          closeModal: true,
        },
      },
      dangerMode: true,
    }).then((willDelete) => {
      if (willDelete) {
        deleteSubDisclosureApiHandler({
          groupingTemplateID: tempId,
          disclosureID: id,
          subNoteID: subid,
        })
          .then((response) => {
            if (response?.data?.success === true) {
              resetItems();
              getTemplateDetails();
            }
          })
          .catch((error) => {
            swal(
              "Error",
              error?.response?.data?.error || "Unable to delete the data",
              "error"
            );
          });
      }
    });
  };

  return (
    <>
      <motion.div
        initial={{ x: 500, y: 500, opacity: 0 }}
        animate={{ x: 0, y: 0, opacity: 1 }}
        exit={{ x: 0, y: 0, opacity: 0 }}
        transition={{ duration: 0.7, ease: "easeInOut" }}
        className={libcss.hierarchyLayout}
        style={{ border: "0px", marginTop: "1%", height: "75vh" }}
      >
        <div
          className={`${libcss.notesSec} ${commoncss.hideScroll}`}
          style={{ width: "40%" }}
        >
          <div className={libcss.levelCatDiv}>
            <div className={libcss.levelCat}>
              <span className={libcss.notesLabel}>
                <span>Disclosures</span>
                {!isReadOnly && (
                  <span
                    className={libcss.addNoteLabel}
                    onClick={handleAddClick}
                    style={{ cursor: "pointer" }}
                  >
                    <PlusIcon /> Add
                  </span>
                )}
              </span>

              <div className={`${libcss.levelCatDiv} ${libcss.notesDiv2}`}>
                {/* Composer Section */}
                {isComposing && !isReadOnly && (
                  <div className={libcss.notesItem}>
                    <input
                      ref={inputRef}
                      value={noteName}
                      onChange={(e) => setNoteName(e.target.value)}
                      onKeyDown={handleKeyDown}
                      placeholder="Type your note..."
                      className={libcss.noteInput}
                    />
                    <div className={libcss.lineActions}>
                      <span
                        className={`${commoncss.clickable}`}
                        onClick={addNotes}
                        disabled={!noteName.trim()}
                      >
                        Add
                      </span>
                      &ensp;
                      <CloseIcon
                        onClick={handleCancel}
                        width={14}
                        height={14}
                      />
                    </div>
                  </div>
                )}

                {/* Existing Notes */}
                {notes &&
                  notes.map((noteItem, i) => (
                    <div key={`note-${noteItem._id || i}`}>
                      <div
                        className={
                          hoveredIndex === i || activeItem === `gs_${i}`
                            ? libcss.notesItem
                            : libcss.notesItemView
                        }
                        onMouseEnter={() => setHoveredIndex(i)}
                        onMouseLeave={() => setHoveredIndex(null)}
                        onClick={() => {
                          setItems(normalizeFromInitial(noteItem));
                          setActiveItem(`gs_${i}`);
                          setSelNote("general");
                          setSelNoteId(noteItem._id);
                          setSelParentId(null); // clear parent when top level selected
                          setOutput(noteItem);
                          setName(noteItem.name);
                        }}
                      >
                        {noteItem.name}
                        {(hoveredIndex === i || activeItem === `gs_${i}`) &&
                          !isReadOnly && (
                            <div className={libcss.lineActions}>
                              <PlusIcon
                                role="button"
                                tabIndex={0}
                                onClick={() => openSubComposer(i)}
                                onKeyDown={(e) =>
                                  (e.key === "Enter" || e.key === " ") &&
                                  openSubComposer(i)
                                }
                              />
                              &ensp;
                              <EditIcon width={15} height={15} />
                              &ensp;
                              <CloseIcon
                                className={commoncss.clickable}
                                width={16}
                                height={16}
                                onClick={() => {
                                  deleteNoteHandler(noteItem._id);
                                }}
                              />
                            </div>
                          )}
                      </div>

                      {subComposeFor === i && !isReadOnly && (
                        <div className={libcss.subNotesItemView}>
                          <input
                            ref={subInputRef}
                            value={subNoteName}
                            onChange={(e) => setSubNoteName(e.target.value)}
                            onKeyDown={handleSubKeyDown}
                            placeholder="Type subnote…"
                            className={libcss.noteInput}
                          />
                          <div className={libcss.lineActions}>
                            <span
                              className={`${commoncss.clickable}`}
                              onClick={() => {
                                addSub(noteItem._id);
                              }}
                              disabled={!subNoteName.trim()}
                            >
                              Add
                            </span>
                            &ensp;
                            <CloseIcon
                              onClick={cancelSubComposer}
                              width={14}
                              height={14}
                            />
                          </div>
                        </div>
                      )}

                      {noteItem?.subNotes?.length > 0 &&
                        noteItem.subNotes.map((subnote, j) => {
                          const subActiveKey = `gs_sn_${i}-${j}`;
                          const subHoverKey = `s-${i}-${j}`;
                          const isActiveOrHover =
                            hoveredIndex === subHoverKey ||
                            activeItem === subActiveKey;

                          return (
                            <div
                              key={`sub-${subnote._id || `${i}-${j}`}`}
                              className={
                                isActiveOrHover
                                  ? libcss.notesItem
                                  : libcss.subNotesItemView
                              }
                              onMouseEnter={() => setHoveredIndex(subHoverKey)}
                              onMouseLeave={() => setHoveredIndex(null)}
                              onClick={() => {
                                setItems(normalizeFromInitial(subnote));
                                setActiveItem(subActiveKey);
                                setSelNote("general_sn");
                                setSelNoteId(subnote._id);
                                setSelParentId(noteItem._id); // <-- parent id needed for sub update
                                setOutput(subnote);
                                setName(subnote.name);
                              }}
                            >
                              {subnote.name}
                              {isActiveOrHover && !isReadOnly && (
                                <div className={libcss.lineActions}>
                                  <EditIcon width={15} height={15} />
                                  &ensp;
                                  <CloseIcon
                                    className={commoncss.clickable}
                                    width={16}
                                    height={16}
                                    onClick={() => {
                                      deleteSubNoteHandler(
                                        noteItem._id,
                                        subnote._id
                                      );
                                    }}
                                  />
                                </div>
                              )}
                            </div>
                          );
                        })}
                    </div>
                  ))}
              </div>
            </div>
          </div>
        </div>
        <div className={libcss.dataDivs}>
          <div className={libcss.levelCatDiv}>
            {/* Flat list of items: editors and tables */}
            <div style={{ display: "grid", gap: 14 }}>
              {items.map((it, idx) => {
                if (it.type === "text") {
                  const html = it.text?.[0]?.content ?? "";
                  return (
                    <div
                      key={it.id}
                      style={{
                        background: "#fff",
                        border: "1px solid #e3efe7",
                        borderRadius: 14,
                        padding: 12,
                        boxShadow: "0 1px 6px rgba(0,0,0,0.04)",
                      }}
                    >
                      <div style={{ marginTop: isReadOnly ? 35 : 10 }}>
                        {!isReadOnly && (
                          <DelIcon
                            className={libcss.textEditorDel}
                            onClick={() => removeItem(it.id)}
                          />
                        )}
                        <QuillReactEditor
                          value={html}
                          onChange={(content) => {
                            updateEditorContent(it.id, content);
                          }}
                          disabled={isReadOnly}
                        />
                      </div>
                    </div>
                  );
                }

                if (it.type === "table") {
                  return (
                    <div
                      key={it.id}
                      style={{
                        background: "#fff",
                        border: "1px solid #e3efe7",
                        borderRadius: 14,
                        padding: 12,
                        boxShadow: "0 1px 6px rgba(0,0,0,0.04)",
                      }}
                    >
                      <div style={{ marginTop: isReadOnly ? 35 : 10 }}>
                        {!isReadOnly && (
                          <DelIcon
                            className={libcss.tableDel}
                            onClick={() => removeItem(it.id)}
                          />
                        )}
                        <ControlledTableGenerator
                          forceOpen={it.id === forceOpenTableId}
                          value={{
                            tableTitle: it.tableTitle,
                            tableRow: it.tableRow,
                            tableColumn: it.tableColumn,
                          }}
                          onChange={(next) =>
                            updateTableItem(it.id, {
                              tableTitle: next.tableTitle,
                              tableRow: next.tableRow,
                              tableColumn: next.tableColumn,
                            })
                          }
                          onAfterOpen={() => {
                            if (forceOpenTableId === it.id)
                              setForceOpenTableId(null);
                          }}
                          disabled={isReadOnly}
                        />
                      </div>
                    </div>
                  );
                }

                return null;
              })}
            </div>
            {selNote && !isReadOnly && (
              <div
                style={{
                  display: "flex",
                  gap: 12,
                  alignItems: "center",
                  padding: 12,
                  borderRadius: 12,
                  marginBottom: 12,
                }}
              >
                <CirclePlus width={37} />
                <AddEditor width={140} onClick={handleAddParagraph} />
                <AddTable width={100} onClick={handleAddTable} />
              </div>
            )}
          </div>
        </div>
      </motion.div>
    </>
  );
}

// export default Notes;
